// Manual-entry page for the v0.3 WebUI MVP.
//
// New-supply calculation only. The form produces a fully-typed
// `DemoNewSupplyFile` (parcel + prices + items) which is then passed
// to the existing core engine via the `runNewSupplyInput` adapter.
// No land-price formula is duplicated here — the adapter calls the
// engine directly.
//
// Layout:
//   1. 宗地基础信息 (parcel basic info form)
//   2. 市场价格 (market price form)
//   3. 市场价格来源说明 (source metadata form, optional)
//   4. 建筑面积分项 (editable building item table — add / delete / edit)
//   5. Calculate button + ResultView re-use

import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { runNewSupplyInput } from "../adapters/runNewSupplyInput.js";
import type { RunFileOutput } from "../adapters/runFile.js";
import { ResultView } from "../components/ResultView.js";
import { buildInlineMarkdown } from "../adapters/buildInlineMarkdown.js";
import type {
  BuildingItem,
  MarketPriceSet,
  Parcel,
} from "../../types/index.js";
import type {
  AffordableHousingType,
  ApplicablePriceType,
  BuildingType,
  CommercialFloor,
  IndustryProjectType,
  PropertyCondition,
  ProjectType,
  SupplyMethod,
  UndergroundType,
} from "../../types/enums.js";

const { Title, Paragraph } = Typography;

const BUILDING_TYPES: readonly BuildingType[] = [
  "厂房", "仓库", "研发用房", "物流建筑", "住宅", "宿舍", "办公",
  "商务公寓", "会议中心", "社会停车场库", "邮政网点", "商业", "加油站",
  "游乐设施", "加气站", "肉菜市场", "旅馆业建筑", "文化设施",
  "医疗卫生设施", "教育设施", "体育设施", "幼儿园托儿所", "文化遗产",
  "宗教建筑", "社会福利设施", "特殊建筑", "科研设施", "公用设施",
  "市政设施", "施工配套设施", "训考场", "殡葬设施", "交通设施", "综合管廊",
];
const PROPERTY_CONDITIONS: readonly PropertyCondition[] = [
  "不得转让", "限整体转让", "允许分割转让",
];
const COMMERCIAL_FLOORS: readonly CommercialFloor[] = [
  "首层商业", "第二层商业", "第三层商业", "第四层及以上商业",
];
const UNDERGROUND_TYPES: readonly UndergroundType[] = [
  "交通设施或综合管廊", "其他地下空间",
];
const AFFORDABLE_HOUSING_TYPES: readonly AffordableHousingType[] = [
  "公共租赁住房",
  "保障性租赁住房-政府组织配租",
  "保障性租赁住房-社会主体出租",
  "配售型保障性住房",
];
const INDUSTRY_PROJECT_TYPES: readonly IndustryProjectType[] = [
  "重点产业项目-联合申请",
  "重点产业项目-单一意向单位",
  "一般产业项目",
  "总部项目",
];
const APPLICABLE_PRICE_TYPES: readonly ApplicablePriceType[] = [
  "住宅", "商业", "办公", "工业", "新型产业", "酒店",
  "公共管理与服务设施", "公用设施",
];
const SUPPLY_METHODS: readonly SupplyMethod[] = [
  "招拍挂", "协议", "租赁", "作价出资", "划拨转有偿", "其他",
];
const PROJECT_TYPES: readonly ProjectType[] = [
  "普通项目", "产业项目", "重点产业项目", "总部项目", "棚户区改造",
  "留用土地", "城市更新", "历史遗留", "其他",
];

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface ParcelForm {
  projectName: string;
  parcelId?: string;
  landArea: number;
  supplyMethod: SupplyMethod;
  calculationType: "新供应土地";
  calculationDate: string;
  mainLandUse: ApplicablePriceType;
  projectType: ProjectType;
  totalGfa: number;
  governmentOwnedGfa?: number;
  remark?: string;
}

interface PricesForm extends MarketPriceSet {}

interface MetadataForm {
  marketPriceSource?: string;
  marketPriceYear?: string;
  valuationDate?: string;
  effectiveDate?: string;
  standardParcelCode?: string;
  benchmarkZoneCode?: string;
  officialQueryUrl?: string;
  priceSourceRemark?: string;
}

interface ItemForm {
  id: string;
  buildingType: BuildingType;
  applicablePriceType?: ApplicablePriceType;
  gfa: number;
  landTermYears: number;
  propertyCondition: PropertyCondition;
  isGovernmentOwned: boolean;
  isUnderground: boolean;
  undergroundType?: UndergroundType;
  commercialFloor?: CommercialFloor;
  isAffordableHousing?: boolean;
  affordableHousingType?: AffordableHousingType;
  isIndustryOriented?: boolean;
  industryProjectType?: IndustryProjectType;
  isSupportingFacility?: boolean;
  supportingFacilityType?: string;
  isCapacityIncrease?: boolean;
  remark?: string;
}

const defaultItem = (idx: number): ItemForm => ({
  id: `item-${idx + 1}`,
  buildingType: "住宅",
  gfa: 10000,
  landTermYears: 70,
  propertyCondition: "允许分割转让",
  isGovernmentOwned: false,
  isUnderground: false,
  applicablePriceType: "住宅",
});

const defaultParcel = (): ParcelForm => ({
  projectName: "新建项目（示例）",
  parcelId: "WEBUI-001",
  landArea: 10000,
  supplyMethod: "招拍挂",
  calculationType: "新供应土地",
  calculationDate: new Date().toISOString().slice(0, 10),
  mainLandUse: "住宅",
  projectType: "普通项目",
  totalGfa: 30000,
  governmentOwnedGfa: 0,
});

const defaultPrices = (): PricesForm => ({
  residential: 25000,
  marketPriceSource: "深圳市自然资源局",
  marketPriceYear: new Date().getFullYear().toString(),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ManualEntryPageProps {
  readonly onResult?: (output: RunFileOutput, fileName: string) => void;
}

export function ManualEntryPage(_props: ManualEntryPageProps): JSX.Element {
  const [parcel, setParcel] = useState<ParcelForm>(defaultParcel);
  const [prices, setPrices] = useState<PricesForm>(defaultPrices);
  const [metadata, setMetadata] = useState<MetadataForm>({});
  const [items, setItems] = useState<ItemForm[]>([defaultItem(0)]);
  const [output, setOutput] = useState<RunFileOutput | null>(null);
  const [inlineMd, setInlineMd] = useState<string>("");

  const handleCalculate = (): void => {
    // Merge metadata into the prices object so the engine sees the
    // same shape that JSON / CSV / xlsx inputs would have.
    const fullPrices: MarketPriceSet = {
      ...prices,
      ...stripEmpty(metadata),
    };
    const fullParcel: Parcel = {
      ...stripEmpty(parcel),
      calculationType: "新供应土地",
    } as Parcel;
    const fullItems: BuildingItem[] = items.map((it) =>
      stripEmpty(it),
    ) as BuildingItem[];

    try {
      const out = runNewSupplyInput({
        parcel: fullParcel,
        prices: fullPrices,
        items: fullItems,
      });
      const md = buildInlineMarkdown(out);
      setOutput(out);
      setInlineMd(md);
      if (out.result.validationMessages.some((m) => m.level === "error")) {
        message.warning(
          `测算完成但有 ${out.result.validationMessages.filter((m) => m.level === "error").length} 处错误`,
        );
      } else {
        message.success("测算完成");
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      message.error(`测算失败：${reason}`);
    }
  };

  return (
    <Space direction="vertical" size={24} style={{ width: "100%" }}>
      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          一、宗地基础信息
        </Title>
        <ParcelFormView value={parcel} onChange={setParcel} />
      </Card>

      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          二、市场价格
        </Title>
        <PricesFormView value={prices} onChange={setPrices} />
      </Card>

      <Card>
        <Title level={4} style={{ marginTop: 0 }}>
          三、市场价格来源说明（可选）
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          记录性元数据，不参与测算；引擎不会抓取 / 调用任何官方查询网站。
        </Paragraph>
        <MetadataFormView value={metadata} onChange={setMetadata} />
      </Card>

      <Card
        title={<Title level={4} style={{ margin: 0 }}>四、建筑面积分项</Title>}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setItems((prev) => [...prev, defaultItem(prev.length)])}
          >
            添加分项
          </Button>
        }
      >
        <ItemTableEditor
          items={items}
          onChange={setItems}
        />
      </Card>

      <Card>
        <Space>
          <Button type="primary" size="large" onClick={handleCalculate}>
            执行测算
          </Button>
          <Button
            size="large"
            onClick={() => {
              setParcel(defaultParcel());
              setPrices(defaultPrices());
              setMetadata({});
              setItems([defaultItem(0)]);
              setOutput(null);
              setInlineMd("");
            }}
          >
            重置
          </Button>
        </Space>
      </Card>

      {output && (
        <ResultView
          result={output.result}
          sourceFileName="手动测算"
          inlineMarkdown={inlineMd}
        />
      )}
    </Space>
  );
}

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

function ParcelFormView({
  value,
  onChange,
}: {
  value: ParcelForm;
  onChange: (v: ParcelForm) => void;
}): JSX.Element {
  const update = (patch: Partial<ParcelForm>): void =>
    onChange({ ...value, ...patch });
  return (
    <Form layout="vertical">
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="项目名称" required>
            <Input
              value={value.projectName}
              onChange={(e) => update({ projectName: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="宗地编号">
            <Input
              value={value.parcelId ?? ""}
              onChange={(e) => update({ parcelId: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="测算时点 (YYYY-MM-DD)" required>
            <Input
              value={value.calculationDate}
              onChange={(e) => update({ calculationDate: e.target.value })}
              placeholder="2026-06-17"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="土地面积 (㎡)" required>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={value.landArea}
              onChange={(v) => update({ landArea: Number(v ?? 0) })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="总建筑面积 (㎡)" required>
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={value.totalGfa}
              onChange={(v) => update({ totalGfa: Number(v ?? 0) })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="产权归政府建筑面积 (㎡)">
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              value={value.governmentOwnedGfa ?? 0}
              onChange={(v) => update({ governmentOwnedGfa: Number(v ?? 0) })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="供应方式" required>
            <Select
              value={value.supplyMethod}
              onChange={(v) => update({ supplyMethod: v as SupplyMethod })}
              options={SUPPLY_METHODS.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="主用途" required>
            <Select
              value={value.mainLandUse}
              onChange={(v) => update({ mainLandUse: v as ApplicablePriceType })}
              options={APPLICABLE_PRICE_TYPES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="项目类型" required>
            <Select
              value={value.projectType}
              onChange={(v) => update({ projectType: v as ProjectType })}
              options={PROJECT_TYPES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="备注">
            <Input.TextArea
              rows={2}
              value={value.remark ?? ""}
              onChange={(e) => update({ remark: e.target.value })}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

function PricesFormView({
  value,
  onChange,
}: {
  value: PricesForm;
  onChange: (v: PricesForm) => void;
}): JSX.Element {
  const update = (patch: Partial<PricesForm>): void =>
    onChange({ ...value, ...patch });
  return (
    <Form layout="vertical">
      <Row gutter={16}>
        {(
          [
            ["residential", "住宅"],
            ["commercial", "商业"],
            ["office", "办公"],
            ["industrial", "工业"],
            ["m0", "新型产业 (M0)"],
            ["hotel", "酒店"],
            ["publicService", "公共管理与服务设施"],
            ["utility", "公用设施"],
          ] as Array<[keyof MarketPriceSet, string]>
        ).map(([key, label]) => (
          <Col xs={12} sm={8} md={6} key={key}>
            <Form.Item label={`${label} (元/㎡)`}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                value={(value[key] as number | undefined) ?? undefined}
                onChange={(v) => update({ [key]: v ?? undefined } as Partial<PricesForm>)}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    </Form>
  );
}

function MetadataFormView({
  value,
  onChange,
}: {
  value: MetadataForm;
  onChange: (v: MetadataForm) => void;
}): JSX.Element {
  const update = (patch: Partial<MetadataForm>): void =>
    onChange({ ...value, ...patch });
  return (
    <Form layout="vertical">
      <Row gutter={16}>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="官方来源">
            <Input
              value={value.marketPriceSource ?? ""}
              onChange={(e) => update({ marketPriceSource: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="发布年份 (4 位)">
            <Input
              value={value.marketPriceYear ?? ""}
              onChange={(e) => update({ marketPriceYear: e.target.value })}
              placeholder="2026"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="评估日期 (YYYY-MM-DD)">
            <Input
              value={value.valuationDate ?? ""}
              onChange={(e) => update({ valuationDate: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="生效日期 (YYYY-MM-DD)">
            <Input
              value={value.effectiveDate ?? ""}
              onChange={(e) => update({ effectiveDate: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="标准宗地号">
            <Input
              value={value.standardParcelCode ?? ""}
              onChange={(e) => update({ standardParcelCode: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Form.Item label="区段编号">
            <Input
              value={value.benchmarkZoneCode ?? ""}
              onChange={(e) => update({ benchmarkZoneCode: e.target.value })}
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="官方查询 URL">
            <Input
              value={value.officialQueryUrl ?? ""}
              onChange={(e) => update({ officialQueryUrl: e.target.value })}
              placeholder="https://..."
            />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item label="来源备注">
            <Input.TextArea
              rows={2}
              value={value.priceSourceRemark ?? ""}
              onChange={(e) => update({ priceSourceRemark: e.target.value })}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Item table editor
// ---------------------------------------------------------------------------

function ItemTableEditor({
  items,
  onChange,
}: {
  items: ItemForm[];
  onChange: (next: ItemForm[]) => void;
}): JSX.Element {
  const updateRow = (idx: number, patch: Partial<ItemForm>): void => {
    onChange(
      items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };
  const deleteRow = (idx: number): void => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const cols: ColumnsType<ItemForm> = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        width: 110,
        render: (_v, _r, idx) => (
          <Input
            value={items[idx].id}
            onChange={(e) => updateRow(idx, { id: e.target.value })}
          />
        ),
      },
      {
        title: "建筑类型",
        width: 130,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].buildingType}
            style={{ width: "100%" }}
            onChange={(v) => updateRow(idx, { buildingType: v as BuildingType })}
            options={BUILDING_TYPES.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "建筑面积 (㎡)",
        width: 130,
        render: (_v, _r, idx) => (
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            value={items[idx].gfa}
            onChange={(v) => updateRow(idx, { gfa: Number(v ?? 0) })}
          />
        ),
      },
      {
        title: "年期",
        width: 90,
        render: (_v, _r, idx) => (
          <InputNumber
            style={{ width: "100%" }}
            min={1}
            value={items[idx].landTermYears}
            onChange={(v) => updateRow(idx, { landTermYears: Number(v ?? 0) })}
          />
        ),
      },
      {
        title: "产权条件",
        width: 140,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].propertyCondition}
            style={{ width: "100%" }}
            onChange={(v) => updateRow(idx, { propertyCondition: v as PropertyCondition })}
            options={PROPERTY_CONDITIONS.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "适用价格类型",
        width: 150,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].applicablePriceType ?? items[idx].buildingType}
            style={{ width: "100%" }}
            allowClear
            onChange={(v) =>
              updateRow(idx, {
                applicablePriceType: (v ?? undefined) as ApplicablePriceType | undefined,
              })
            }
            options={APPLICABLE_PRICE_TYPES.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "政府产权",
        width: 80,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isGovernmentOwned}
            onChange={(e) => updateRow(idx, { isGovernmentOwned: e.target.checked })}
          />
        ),
      },
      {
        title: "地下",
        width: 70,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isUnderground}
            onChange={(e) => updateRow(idx, { isUnderground: e.target.checked })}
          />
        ),
      },
      {
        title: "商业楼层",
        width: 140,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].commercialFloor}
            style={{ width: "100%" }}
            allowClear
            onChange={(v) =>
              updateRow(idx, { commercialFloor: (v ?? undefined) as CommercialFloor | undefined })
            }
            options={COMMERCIAL_FLOORS.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "地下类型",
        width: 160,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].undergroundType}
            style={{ width: "100%" }}
            allowClear
            onChange={(v) =>
              updateRow(idx, { undergroundType: (v ?? undefined) as UndergroundType | undefined })
            }
            options={UNDERGROUND_TYPES.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "保障性住房",
        width: 100,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isAffordableHousing ?? false}
            onChange={(e) => updateRow(idx, { isAffordableHousing: e.target.checked })}
          />
        ),
      },
      {
        title: "保障房类型",
        width: 200,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].affordableHousingType}
            style={{ width: "100%" }}
            allowClear
            onChange={(v) =>
              updateRow(idx, {
                affordableHousingType: (v ?? undefined) as AffordableHousingType | undefined,
              })
            }
            options={AFFORDABLE_HOUSING_TYPES.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "产业导向",
        width: 90,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isIndustryOriented ?? false}
            onChange={(e) => updateRow(idx, { isIndustryOriented: e.target.checked })}
          />
        ),
      },
      {
        title: "产业项目类型",
        width: 200,
        render: (_v, _r, idx) => (
          <Select
            value={items[idx].industryProjectType}
            style={{ width: "100%" }}
            allowClear
            onChange={(v) =>
              updateRow(idx, {
                industryProjectType: (v ?? undefined) as IndustryProjectType | undefined,
              })
            }
            options={INDUSTRY_PROJECT_TYPES.map((s) => ({ label: s, value: s }))}
          />
        ),
      },
      {
        title: "配套",
        width: 70,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isSupportingFacility ?? false}
            onChange={(e) => updateRow(idx, { isSupportingFacility: e.target.checked })}
          />
        ),
      },
      {
        title: "配套类型",
        width: 130,
        render: (_v, _r, idx) => (
          <Input
            value={items[idx].supportingFacilityType ?? ""}
            placeholder="如：配套商业"
            onChange={(e) => updateRow(idx, { supportingFacilityType: e.target.value })}
          />
        ),
      },
      {
        title: "提容",
        width: 70,
        render: (_v, _r, idx) => (
          <Checkbox
            checked={items[idx].isCapacityIncrease ?? false}
            onChange={(e) => updateRow(idx, { isCapacityIncrease: e.target.checked })}
          />
        ),
      },
      {
        title: "备注",
        width: 150,
        render: (_v, _r, idx) => (
          <Input
            value={items[idx].remark ?? ""}
            onChange={(e) => updateRow(idx, { remark: e.target.value })}
          />
        ),
      },
      {
        title: "操作",
        width: 60,
        fixed: "right",
        render: (_v, _r, idx) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteRow(idx)}
          />
        ),
      },
    ],
    [items],
  );

  if (items.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message="暂无分项"
        description="点击「添加分项」新增建筑面积分项后即可测算。"
      />
    );
  }

  return (
    <Table<ItemForm>
      rowKey="id"
      size="small"
      columns={cols}
      dataSource={items}
      pagination={false}
      scroll={{ x: 2200 }}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripEmpty<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "number" && !Number.isFinite(v)) continue;
    out[k] = v;
  }
  return out as T;
}
